/**
 * Queue routing helpers for the Workspace Initialization Invariant.
 * Resolves the workspace org root queue so new items can be routed by default.
 */

import { useEnterpriseIdentityStore } from '../stores/prioritization/enterpriseIdentityStore';
import { getCanonicalWorkspaceOrgRoot } from './orgUnitTree';

/**
 * Returns the root queue (org unit) id for the given workspace, if one exists and is enabled.
 * Uses the canonical workspace organization root (canon 10P), not legacy workspace-queue duplicates.
 */
export function getRootQueueIdForWorkspace(workspaceId: string): string | null {
  const orgUnits = useEnterpriseIdentityStore.getState().orgUnits;
  const root = getCanonicalWorkspaceOrgRoot(Array.from(orgUnits.values()), workspaceId);
  if (!root?.queueSettings?.enabled) return null;
  return root.id;
}

/**
 * The default owner configured on a queue, or null.
 *
 * `queueSettings.defaultStewardId` shipped on the type long before anything read
 * it. It is wired now: see `applyQueueDefaultOwner`.
 */
export function getQueueDefaultOwnerId(queueId: string | null | undefined): string | null {
  if (!queueId) return null;
  const orgUnits = useEnterpriseIdentityStore.getState().orgUnits;
  const unit = orgUnits.get(queueId);
  if (!unit?.queueSettings?.enabled) return null;
  return unit.queueSettings.defaultStewardId ?? null;
}

/**
 * Owner to apply when an item lands in a queue — or null to leave it alone.
 *
 * The whole rule in one place: a queue's default owner fills an EMPTY owner and
 * never replaces one. Returning null for an item that already has an owner is
 * what makes "moved into a queue" safe to call from anywhere, including bulk
 * moves and triage redirects.
 */
export function resolveOwnerOnQueueMove(
  targetQueueId: string | null | undefined,
  currentOwnerId: string | null | undefined,
): string | null {
  if (currentOwnerId) return null;
  return getQueueDefaultOwnerId(targetQueueId);
}
